import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import { resolveImg } from "@/lib/content";

const ease = [0.16, 1, 0.3, 1];

export const VideoSection = ({ c }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const url = resolveImg(c?.video_url);
  const poster = resolveImg(c?.hero_image);

  if (!url) return null;

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    if (!v.muted) v.play().catch(() => {});
    setMuted(v.muted);
  };

  return (
    <section
      id="video"
      data-testid="video-section"
      className="bg-[#8E1226] py-24 md:py-36 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease }}
            className="md:col-span-6 lg:col-span-7"
          >
            <span className="text-xs uppercase tracking-[0.32em] text-[#FBF7F0]/70">
              {c?.video_eyebrow}
            </span>
            <h2 className="mt-6 font-serif font-light text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight text-[#FBF7F0]">
              {c?.video_title}
            </h2>
            <p className="mt-8 text-[#FBF7F0]/80 text-base md:text-lg leading-relaxed max-w-md">
              {c?.video_paragraph}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15, ease }}
            className="md:col-span-6 lg:col-span-5 flex md:justify-end"
          >
            <div className="relative w-full max-w-[380px]">
              <div className="overflow-hidden rounded-2xl border border-[#FBF7F0]/20 shadow-2xl">
                <video
                  ref={videoRef}
                  data-testid="video-player"
                  src={url}
                  poster={poster || undefined}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="w-full h-auto max-h-[70vh] object-cover"
                />
              </div>
              <button
                type="button"
                data-testid="video-unmute-btn"
                onClick={toggleSound}
                aria-label={muted ? "Unmute video" : "Mute video"}
                className="absolute bottom-4 right-4 inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#FBF7F0] text-[#8E1226] shadow-lg hover:scale-105 transition-transform"
              >
                {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
